import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, SimpleGrid, FormLabel, Spinner } from "@chakra-ui/react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { FiExternalLink, FiInfo, FiMapPin, FiPhone, FiZoomIn, FiZoomOut } from 'react-icons/fi'
import hotToast from 'react-hot-toast';
import Select from 'react-select';
import useScreenType from "react-screentype-hook";

import { Location, Organization } from "../../../redux/reducers/types";
import { useGetOrganizationsQuery, useGetSparksQuery } from "../../../redux/reducers/apiSlice";
import { Modal } from "../../Components/Modal/Modal";
import Tooltip from "../../Components/Tooltip";
import { Link } from "react-router-dom";

const US_BOUNDS: L.LatLngBoundsExpression = [
  [24.396308, -125.0], // SW
  [49.384358, -66.93457], // NE
];

export function SetMapRef({ setMapRef }: { setMapRef: (map: L.Map) => void }) {
  const map = useMap();

  useEffect(() => {
    setMapRef(map);
  }, [map, setMapRef]);

  return null;
}

const ZoomToMarker = ({ position, onDetailsClicked, data, zoomedIn, setZoomedin }: { position: [number, number], onDetailsClicked: () => void ,data: Organization, zoomedIn: boolean, setZoomedin: (val: boolean) => void }  ) => {
  const map = useMap();



  const onZoomIn = () => {
    map.flyTo(position, 16, { animate: true, duration: 1 });
    setZoomedin(true);
  };

  const onZoomOut = () => {
    map.flyTo([39.8283, -98.5795], 4, { animate: true, duration: 1 });
    setZoomedin(false);
  };

  return <Marker  riseOnHover={true} position={position}>
      <Popup>
        <h2 style={{textAlign: 'center', fontSize: 24, marginBottom: 12}}>{data.name}</h2>
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 8, alignItems: 'center'}}>
            {!zoomedIn && (
                <Button onClick={onZoomIn}>
                <span style={{marginRight: 8}}><FiZoomIn/></span> Zoom in to location
                </Button>
            )}
            {zoomedIn && (
                <Button onClick={onZoomOut}>
                    <span style={{marginRight: 8}}><FiZoomOut/></span> Zoom out from location
                </Button>
            )}
          <Button onClick={onDetailsClicked}>
            View details
          </Button>
        </div>
      </Popup>
    </Marker>;
};

const SparksMap = () => {

    // queries
    const { data: orgData, isLoading: isLoadingOrgData } = useGetOrganizationsQuery()
    const { data: sparksData, isLoading: sparksDataLoading } = useGetSparksQuery();
    const { isMobile } = useScreenType();
    const [zoomedIn, setZoomedin] = useState(false);

    //states
    const [selectedOrgModal, setSelectedOrgModal] = useState<Organization | null>(null);
    const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
    const [fullScreenMode, setFullScreenMode] = useState(false);
    const [filteredOrgData, setFilteredOrgData] = useState<Organization[]>([])
    const [selectedSpark, setSelectedSpark] = useState<string | null>(null);
    const navigate = useNavigate();

    // refs
    const mapRef = useRef<L.Map | null>(null);

    useEffect(() => {
        if(orgData){
            setFilteredOrgData(orgData);
        }
    },[orgData])

    useEffect(() => {
        if(selectedSpark){
           const filteredBySparks =  orgData.filter((org => org.sparks && org.sparks.includes(selectedSpark)))
           if(filteredBySparks.length === 0){
            hotToast('No organization with that spark exist yet');
           } else {
            zoomToUS();
            hotToast.success('Map updated to show only the organizations with the spark selected', { duration: 3000 });
            zoomToUS();
           }
           console.log("FILTERED BY SPARKS: ", filteredBySparks);
           setFilteredOrgData(filteredBySparks)
        } else {
            setFilteredOrgData(orgData);
        }
    },[selectedSpark])


    const positions: [number, number][] = useMemo(() => {
        if(filteredOrgData && filteredOrgData.length > 0){
            return filteredOrgData.filter((org) => org.location.latitude && org.location.longitude).map((org) => [parseFloat(org.location.latitude), parseFloat(org.location.longitude)]);
        }
        return []
    },[filteredOrgData])

    console.log("POSITIONS: ", positions);

    const sparkOptions: { label: string; value: string }[] = useMemo(() => {
        return sparksData && sparksData.length && sparksData.map(spark => (
        {label: spark.name, value: spark.name }
        )) || []
    },[sparksData])

    const orgOptions: { label: string; value: string }[] = useMemo(() => {
        return orgData && orgData.length && orgData.map(org => (
        {label: org.name, value: org.name }
        )) || []
    },[orgData])

     const zoomToUS = () => {
        if (mapRef.current) {
            mapRef.current.flyTo([39.8283, -98.5795], 4, { animate: true, duration: 1 });
            setZoomedin(false);
        }
     };

     const setMapRef = (map: L.Map) => {
        mapRef.current = map;
    };

    console.log("Position", positions);
    console.log("org to show", selectedOrgModal);

    const generateFullAddress = (location: Location) => {
        return `${location.address1},${location.city}, ${location.state}, ${location.zip}`
    }

    const onSparkSelect = (option: { label: string; value: string; }, actionMeta) => {
        console.log("ACTION META", actionMeta);
        if(actionMeta.action === 'clear'){
            setSelectedSpark(null)
        } else {
            setSelectedSpark(option.value);
        }
    }

    const onOrgSelect = (orgOption: { label: string; value: string; }, actionMeta) => {
        
        if(actionMeta.action == 'clear'){
            zoomToUS()
            setSelectedOrg(null);
        } else {
            setSelectedOrg(orgOption.value);
            if(mapRef.current){
                if(orgData){
                    const org = orgData.find(_org => _org.name === orgOption.value)
                    if(org && org.location.latitude && org.location.longitude){
                        mapRef.current.flyTo([parseFloat(org.location.latitude), parseFloat(org.location.longitude)], 16, { animate: true, duration: 1})
                    } else {
                        hotToast("This organization has no known GPS location at this time.");
                    }
                }
            }
        }
    }

    
    if(isLoadingOrgData || !positions) return <div style={{width: '100%', height: '80vh',marginTop: 24,display: 'flex', justifyContent: 'center', alignItems: 'center'}}>Loading map... <Spinner /></div>
    
    return <div style={{marginBottom: 24, padding: isMobile ? 8: 24}}>
        <header style={{ justifyContent: 'center', alignItems: 'center',padding: 24, marginTop: 24, marginBottom: 24, borderBottom: '1px solid #aaa'}}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent:'center'}}>
                <h1 style={{fontFamily: 'Montserrat', fontSize: 24, textAlign: 'center', fontWeight: 'bold', color: 'rgb(238, 90, 41)', marginRight: 8}}>Sparks Map</h1>
                <Tooltip 
                    message={
                        <div style={{textAlign: 'left'}}>
                            <p>
                                if you want your business or organization to be featured: 
                            </p>
                            <ul style={{listStyleType: 'circle', listStylePosition: 'outside', paddingLeft: 8, marginTop: 8}}>
                                <li style={{marginLeft: 8, marginBottom: 4}}>Go to My organizations</li> 
                                <li style={{marginLeft: 8, marginBottom: 4}}>Click on the organization you want to update</li>
                                <li style={{marginLeft: 8, marginBottom: 4}}>Click on the 3 dots</li>
                                <li style={{marginLeft: 8, marginBottom: 4}}>Choose Edit organization</li>
                                <li style={{marginLeft: 8, marginBottom: 4}}>I want to update my location</li>
                            </ul>
                        </div>
                    }>
                    <FiInfo size={24} />
                </Tooltip>
            </div>
            <div style={{marginTop: 24}}>
                <p style={{textAlign: 'center', color: '#666'}}>Discover our organizations and businesses location! </p>
               
                {/* <p style={{textAlign: 'center', color: '#666'}}>if you want your business or organization to be featured. Go to <Link to="/my-organizations">My organizations</Link> &gt; Click on the 3 dots &gt; Choose "Edit organization" &gt; I want to update my location </p> */}
            </div>
        </header>
        
        <SimpleGrid
            style={{width: '100%', position: 'relative', zIndex: 1001}}
            gap={4} 
            columns={{base: 1, md: 1, sm: 1}}
        >            

            <Box>
                <FormLabel>Filter by spark</FormLabel>
                <Select
                    className="basic-single"
                    classNamePrefix="select"
                    // defaultValue={sparkOptions[0]}
                    placeholder="Select a spark"
                    isDisabled={false}
                    isLoading={false}
                    isClearable={!!selectedSpark}
                    onChange={onSparkSelect}
                    isRtl={false}
                    isSearchable={true}
                    name="color"
                    options={sparkOptions}
                />
            </Box>
            <Box>
                 <FormLabel>Search organization</FormLabel>
                <Select
                    className="basic-single"
                    classNamePrefix="select"
                    // defaultValue={sparkOptions[0]}
                    placeholder="Select an organization"
                    isDisabled={false}
                    isLoading={false}
                    isClearable={!!selectedOrg}
                    onChange={onOrgSelect}
                    isRtl={false}
                    isSearchable={true}
                    name="color"
                    options={orgOptions}
                />
            </Box>
            <Box>
                <Button onClick={zoomToUS}>
                    Reset map position
                </Button>
            </Box>   
        </SimpleGrid>
            <div style={{ position:fullScreenMode === true ? 'absolute' :'static', width: '100%', height: fullScreenMode ? '100vh' : '60vh', overflow: 'scroll', marginTop: 12, marginBottom: 12, zIndex: 100000}}>
            <MapContainer
                center={positions?.[0] || [39.8283, -98.5795]}
                zoom={4}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
                >
                <SetMapRef setMapRef={setMapRef} />
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {filteredOrgData && filteredOrgData.length > 0 && filteredOrgData?.map((org, id) => {

                    const longitude = parseFloat(org.location.longitude); 
                    const latitude = parseFloat(org.location.latitude); 

                    if(!longitude && !latitude) return null;
                    return (
                    <ZoomToMarker zoomedIn={zoomedIn} setZoomedin={setZoomedin} key={id} position={[latitude, longitude]} data={org} onDetailsClicked={() => setSelectedOrgModal(org)} />
                )})}

            </MapContainer>
        </div>
        <Modal
             modalTitle={selectedOrgModal?.name ? `Organization name: ${selectedOrgModal.name}` : "Organization"}
             isOpen={!!selectedOrgModal?.name}
             onClose={() => setSelectedOrgModal(null)}
            >
                {selectedOrgModal && (
                <div style={{ padding: "24px" }}>
                    {/* {selectedOrgModal.id && (
                        <div style={{marginTop: 12}}>
                            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center'}}>
                                <p style={{fontWeight: 'bold', marginRight: 8}}>Profile Link</p>
                                <Link target="_blank" to={`/organizations/${selectedOrgModal.id}/profile`}>
                                    <AiOutlineLink />
                                </Link>
                            </div>

                        </div>
                    )} */}
                    {/* {selectedOrgModal.mission && (
                        <div style={{marginTop: 12}}>
                            <p style={{fontWeight: 'bold'}}>Mission</p>
                            <div style={{marginTop: 8}}>
                                <p>{selectedOrgModal.mission}</p>
                            </div>
                        </div>
                    )}
                    {selectedOrgModal.problem && (
                        <div style={{marginTop: 12}}>
                            <p style={{fontWeight: 'bold'}}>Problem statement</p>
                            <div style={{marginTop: 8}}>
                                <p>{selectedOrgModal.problem}</p>
                            </div>
                        </div>
                    )}
                    {selectedOrgModal.vision && (
                        <div style={{marginTop: 12}}>
                            <p style={{fontWeight: 'bold'}}>Vision</p>
                            <div style={{marginTop: 8}}>
                                <p>{selectedOrgModal.problem}</p>
                            </div>
                        </div>
                    )} */}
                    {/* {selectedOrgModal.sparks && (
                         <div style={{marginTop: 12}}>
                            <p style={{fontWeight: 'bold'}}>Sparks</p>
                            <div style={{marginTop: 8}}>
                                <p>{selectedOrgModal.sparks.join(', ')}</p>
                            </div>
                        </div>
                    )} */}

                    <div style={{ display: 'flex',flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap:16, marginBottom: 24,}}>
                            {selectedOrgModal.sparks.map((sparkName) => {

                                const spark = sparksData.find(_spark => _spark.name === sparkName);

                                
                                if(spark){
                                    return <Tooltip message={spark.name}><div style={{width: '32px', height: 'auto'}}><img src={spark.logo} /></div></Tooltip>
                                }
                                
                                return null;
                            })}
                    </div>
                    
                    <button onClick={() =>navigate(`/organizations/${selectedOrgModal.id}/profile`)} style={{width: '100%',border: '1px solid #eee', backgroundColor: 'white', borderRadius: 0, display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 8, padding: "12px"}}>
                            <div style={{marginRight: 12}}>
                                <FiExternalLink size={24}  />
                            </div>
                            <p>
                                Visit profile
                            </p>
                    </button>
                    <div style={{width: '100%',border: '1px solid #eee', backgroundColor: 'white', borderRadius: 0, display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 8, padding: "12px", marginTop: 12}}>
                            <div style={{marginRight: 12}}>
                                <FiPhone  size={24} />
                            </div>
                            <p>
                                {selectedOrgModal.phoneNumber}
                            </p>
                    </div>
                    <div style={{marginTop: 12}}>
                            <Button
                                style={{width: '100%',border: '1px solid #eee', backgroundColor: 'white', borderRadius: 0, justifyContent: 'flex-start'}}
                                onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(generateFullAddress(selectedOrgModal.location))}`, '_blank')}>
                                <span style={{marginRight: 12}}>
                                    <FiMapPin size={24}/>
                                </span>
                                <p style={{fontWeight: 'normal'}}>
                                    {selectedOrgModal.location.address1},{selectedOrgModal.location.city}, {selectedOrgModal.location.state}, {selectedOrgModal.location.zip}
                                </p>
                            </Button>
                        {/* </div> */}
                    </div>
                </div>
                )}
            </Modal>
    </div>
}

export default SparksMap;